import React, { useState } from 'react';
import CookieConsent from 'react-cookie-consent';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function CookieBanner() {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const handleAccept = async () => {
    console.log('[CookieBanner] Accept all - including geolocation');
    
    // Enable analytics
    localStorage.setItem('analytics-consent', 'true');
    
    // Enable geolocation
    localStorage.setItem('geolocation-consent', 'true');
    
    // Request geolocation permission
    await requestGeolocation();
    
    // Initialize PostHog if available
    if (window.posthog && typeof window.posthog.init === 'function') {
      window.posthog.init("phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE", {
        api_host: "https://eu.i.posthog.com",
        person_profiles: "identified_only",
      });
    }
  };

  const handleDecline = () => {
    console.log('[CookieBanner] Decline optional - only necessary cookies');
    
    // Disable analytics
    localStorage.setItem('analytics-consent', 'false');
    
    // Disable geolocation
    localStorage.setItem('geolocation-consent', 'false');
    
    // Opt out of PostHog if loaded
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
  };

  const requestGeolocation = async () => {
    try {
      if ('geolocation' in navigator) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            console.log('[CookieBanner] Geolocation granted:', position.coords);
            // Store geolocation data
            const geoData = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              timestamp: new Date().toISOString()
            };
            localStorage.setItem('geolocation-data', JSON.stringify(geoData));
            
            // Trigger custom event for country detection
            window.dispatchEvent(new CustomEvent('geolocation-updated', { detail: geoData }));
          },
          (error) => {
            console.warn('[CookieBanner] Geolocation denied or failed:', error.message);
            localStorage.setItem('geolocation-consent', 'denied');
          },
          {
            enableHighAccuracy: false,
            timeout: 10000,
            maximumAge: 86400000 // 24 hours cache
          }
        );
      }
    } catch (error) {
      console.error('[CookieBanner] Geolocation error:', error);
    }
  };

  return (
    <CookieConsent
      location="bottom"
      buttonText={t('cookies.accept', 'Alle akzeptieren')}
      declineButtonText={t('cookies.decline', 'Nur notwendige')}
      enableDeclineButton
      cookieName="ivoliq_cookie_consent"
      onAccept={handleAccept}
      onDecline={handleDecline}
      style={{
        background: '#1f2937',
        padding: '1.5rem 2rem',
        alignItems: 'center',
        boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
      }}
      buttonStyle={{
        background: '#2563eb',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: '600',
        padding: '0.75rem 2rem',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        marginRight: '1rem',
      }}
      declineButtonStyle={{
        background: '#6b7280',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: '500',
        padding: '0.75rem 2rem',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
      }}
      expires={365}
    >
      <div className="text-sm text-white">
        <p className="mb-2">
          <strong>{t('cookies.title', 'Wir verwenden Cookies & Standortdaten')}</strong>
        </p>
        <p className="text-gray-300 mb-2">
          {t('cookies.message', 'Wir verwenden notwendige Cookies für die Funktionalität der Website und optionale Cookies für Analysen.')}{' '}
        </p>
        
        {/* Details toggle */}
        <button 
          onClick={() => setShowDetails(!showDetails)}
          className="text-blue-400 hover:text-blue-300 text-xs mb-2 flex items-center"
        >
          {showDetails ? '▼' : '▶'} {t('cookies.showDetails', 'Details anzeigen')}
        </button>
        
        {showDetails && (
          <div className="text-xs text-gray-400 space-y-2 mb-3 pl-4 border-l-2 border-gray-600">
            <div>
              <strong className="text-gray-300">📍 {t('cookies.geolocation', 'Standorterkennung')}</strong>
              <p>{t('cookies.geolocationDesc', 'Zur präzisen Erkennung Ihres Landes für steuerrechtliche Einstellungen. Verbessert die automatische Ländererkennung.')}</p>
            </div>
            <div>
              <strong className="text-gray-300">📊 {t('cookies.analytics', 'Analyse-Cookies')}</strong>
              <p>{t('cookies.analyticsDesc', 'Hilft uns die Nutzung zu verstehen und die App zu verbessern.')}</p>
            </div>
          </div>
        )}
        
        <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline text-xs">
          {t('cookies.learnMore', 'Mehr erfahren')}
        </Link>
      </div>
    </CookieConsent>
  );
}
